import { useMemo } from "react";
import type { ReactElement } from "react";

import type { CatalogEntry, SuiteName } from "../api/types";
import { SUITE_LABELS } from "./SuiteBadge";

interface Props {
  /** Полный список продуктов из /api/catalog */
  catalog: CatalogEntry[];
  /** Текущий выбранный subset (slugs). Пустой массив или null = все */
  selected: string[];
  /** Callback при смене selection */
  onChange: (newSelected: string[]) => void;
}

/**
 * Click-based picker для invite catalog_subset.
 *
 * Per CEO mandate (2026-04-27): "менеджер должен выбирать продукты кликами,
 * а не текстом". Заменяет старый comma-separated text input.
 *
 * Features:
 * - Группировка чекбоксов по suite (Safety / Logistics / Sales / etc.)
 * - Quick presets ("All Safety", "All Logistics", "All", "Reset")
 * - Counter "Выбрано: N из M"
 * - Suite-level "Select all in suite" checkbox
 */
export function InviteCatalogPicker({
  catalog,
  selected,
  onChange,
}: Props): ReactElement {
  // Группировка по suite + sort внутри suite по name
  const grouped = useMemo(() => {
    const result: Record<string, CatalogEntry[]> = {};
    for (const e of catalog) {
      (result[e.suite] ??= []).push(e);
    }
    for (const k of Object.keys(result)) {
      result[k]!.sort((a, b) => a.name.localeCompare(b.name));
    }
    return result;
  }, [catalog]);

  const suiteOrder: SuiteName[] = [
    "Safety",
    "Logistics",
    "Sales",
    "Mining",
    "Orchestration",
    "Industrial",
    "Tooling",
  ];

  const total = catalog.length;
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggleSlug(slug: string) {
    if (selectedSet.has(slug)) {
      onChange(selected.filter((s) => s !== slug));
    } else {
      onChange([...selected, slug]);
    }
  }

  function toggleSuite(suite: SuiteName) {
    const slugsInSuite = grouped[suite]?.map((e) => e.slug) ?? [];
    const allSelected = slugsInSuite.every((s) => selectedSet.has(s));
    if (allSelected) {
      // Снять весь suite
      onChange(selected.filter((s) => !slugsInSuite.includes(s)));
    } else {
      // Выбрать весь suite (preserve existing other suites)
      const newSet = new Set(selected);
      for (const s of slugsInSuite) newSet.add(s);
      onChange(Array.from(newSet));
    }
  }

  function applyPreset(preset: "all" | "safety" | "logistics" | "sales" | "mining-safety" | "reset") {
    switch (preset) {
      case "all":
        onChange(catalog.map((e) => e.slug));
        break;
      case "safety":
        onChange(grouped.Safety?.map((e) => e.slug) ?? []);
        break;
      case "logistics":
        onChange(grouped.Logistics?.map((e) => e.slug) ?? []);
        break;
      case "sales":
        onChange(grouped.Sales?.map((e) => e.slug) ?? []);
        break;
      case "mining-safety":
        onChange([
          ...(grouped.Mining?.map((e) => e.slug) ?? []),
          ...(grouped.Safety?.map((e) => e.slug) ?? []),
        ]);
        break;
      case "reset":
        onChange([]);
        break;
    }
  }

  return (
    <div className="catalog-picker">
      <div className="catalog-picker__header">
        <span className="catalog-picker__title">
          Продукты в этом invite
        </span>
        <span className="catalog-picker__counter">
          Выбрано:{" "}
          <strong>
            {selected.length === 0 ? `все ${total}` : `${selected.length} из ${total}`}
          </strong>
        </span>
      </div>

      {/* Quick presets */}
      <div className="catalog-picker__presets">
        <button
          type="button"
          className="catalog-picker__preset"
          onClick={() => applyPreset("all")}
        >
          Все продукты ({total})
        </button>
        <button
          type="button"
          className="catalog-picker__preset"
          onClick={() => applyPreset("safety")}
          style={{ borderColor: "#dc2626" }}
        >
          Safety Suite ({grouped.Safety?.length ?? 0})
        </button>
        <button
          type="button"
          className="catalog-picker__preset"
          onClick={() => applyPreset("logistics")}
          style={{ borderColor: "#f59e0b" }}
        >
          Logistics Suite ({grouped.Logistics?.length ?? 0})
        </button>
        <button
          type="button"
          className="catalog-picker__preset"
          onClick={() => applyPreset("sales")}
          style={{ borderColor: "#16a34a" }}
        >
          Sales Intelligence ({grouped.Sales?.length ?? 0})
        </button>
        <button
          type="button"
          className="catalog-picker__preset"
          onClick={() => applyPreset("mining-safety")}
          style={{ borderColor: "#0891b2" }}
        >
          Mining + Safety ({(grouped.Mining?.length ?? 0) + (grouped.Safety?.length ?? 0)})
        </button>
        <button
          type="button"
          className="catalog-picker__preset catalog-picker__preset--danger"
          onClick={() => applyPreset("reset")}
        >
          Сбросить
        </button>
      </div>

      <p className="catalog-picker__hint">
        Если оставить пустым — клиент увидит все {total} продуктов.
      </p>

      {/* Suite groups with checkboxes */}
      <div className="catalog-picker__suites">
        {suiteOrder.map((suite) => {
          const products = grouped[suite] ?? [];
          if (products.length === 0) return null;

          const slugsInSuite = products.map((p) => p.slug);
          const allSuiteSelected = slugsInSuite.every((s) => selectedSet.has(s));
          const someSuiteSelected = slugsInSuite.some((s) => selectedSet.has(s));

          return (
            <fieldset key={suite} className="catalog-picker__suite">
              <legend className="catalog-picker__suite-legend">
                <label className="catalog-picker__suite-checkbox">
                  <input
                    type="checkbox"
                    checked={allSuiteSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSuiteSelected && !allSuiteSelected;
                    }}
                    onChange={() => toggleSuite(suite)}
                  />
                  <strong>{SUITE_LABELS[suite]}</strong>
                  <span className="catalog-picker__suite-count">
                    {slugsInSuite.filter((s) => selectedSet.has(s)).length} / {products.length}
                  </span>
                </label>
              </legend>
              <div className="catalog-picker__products">
                {products.map((p) => (
                  <label
                    key={p.slug}
                    className={
                      selectedSet.has(p.slug)
                        ? "catalog-picker__product catalog-picker__product--selected"
                        : "catalog-picker__product"
                    }
                    style={
                      selectedSet.has(p.slug)
                        ? { borderColor: p.accent_color }
                        : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selectedSet.has(p.slug)}
                      onChange={() => toggleSlug(p.slug)}
                    />
                    <span className="catalog-picker__product-info">
                      <strong>{p.name}</strong>
                      <span>{p.tagline}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}
